/**
 * The Solenex platform console's client.
 *
 * Everything here goes through Cloud Functions rather than the Firestore SDK.
 * The console reads across every tenant, and no Firestore rule can express that
 * without leaving a permanent cross-tenant hole in the ruleset — so
 * platformOrgStats, platformAdmins, impersonationSessions and adminAuditLog are
 * all closed to clients and reached only through the admin* callables, which
 * re-check the allowlist on every call.
 */

import { Injectable, inject, signal, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from './telemetry';
import { OrgContextService } from './org-context.service';
import { environment } from '../../../environments/environment';

export type OrgType = 'pco' | 'standalone';

export interface OrgStatsPhoneNumber {
    id: string;
    displayPhoneNumber: string | null;
    verifiedName: string | null;
    qualityRating: string | null;
    status: string | null;
    messagingLimitTier: string | null;
}

/**
 * One organisation's rolled-up statistics.
 *
 * Every date is epoch milliseconds, not a Date or a Timestamp — the callable
 * protocol cannot carry a Firestore Timestamp, so the server converts on the
 * way out.
 */
/** How an onboarding step actually ended. */
export type OnboardingStepStatus = 'completed' | 'skipped' | 'not_applicable' | 'outstanding';

export interface OrgStatsOnboardingStep {
    id: string;
    status: OnboardingStepStatus;
    at: number | null;
    /**
     * False when the status was inferred from the organisation's data rather
     * than read from a recorded outcome — true for anyone who onboarded before
     * onboarding.stepOutcomes existed.
     */
    recorded: boolean;
}

export interface OrgStats {
    orgId: string;
    name: string | null;
    orgType: OrgType;
    countryCode: string | null;
    timezone: string | null;
    createdAt: number | null;

    onboarding: {
        complete: boolean;
        completedAt: number | null;
        completedSteps: string[];
        requiredSteps: string[];
        outstandingSteps: string[];
        /**
         * Per-step status. Distinct from completedSteps, which records a
         * skipped step identically to a completed one — the reason every
         * organisation used to show a full row of green ticks.
         */
        steps: OrgStatsOnboardingStep[];
    };

    whatsapp: {
        connected: boolean;
        status: string | null;
        connectedAt: number | null;
        messagingTier: string | null;
        phoneNumbers: OrgStatsPhoneNumber[];
    };

    subscription: {
        status: string | null;
        planId: string | null;
        /** Resolved from the `plans` collection server-side; null when unknown. */
        planName: string | null;
        trialEndDate: number | null;
        nextBillingDate: number | null;
    };

    counts: {
        members: number;
        contacts: number;
        contactLists: number;
        pcoPeople: number;
        templates: number;
        templatesApproved: number;
        campaigns: number;
        campaignsRecent: number;
        conversations: number;
        conversationsOpen: number;
        /** Both stores: the org's scheduledMessages and top-level `schedules`. */
        scheduledMessages: number;
        automations: number;
        apiApps: number;
    };

    recentSends: { sent: number; failed: number; windowDays: number };

    activity: {
        lastCampaignAt: number | null;
        lastConversationAt: number | null;
        lastSignInAt: number | null;
    };

    syncStatus: string | null;
    /** Planning Center sync health; null for standalone organisations. */
    pcoSync?: PcoSyncSummary | null;
    computedAt: number | null;
}

/** Mirrors PcoSyncSummary in functions/src/services/platform-admin/stats.ts. */
export interface PcoSyncSummary {
    status: string | null;
    error: string | null;
    startedAt: number | null;
    lastSyncedAt: number | null;
    /** The step the latest run reached: the one it failed on, if it failed. */
    step: string | null;
    jobType: string | null;
    counts: Record<string, number>;
    /** Finished without Groups because Planning Center refused them. */
    groupsSkipped: boolean;
    /** The webhook owner still has a Planning Center sign-in. */
    tokenAvailable: boolean;
}

export interface FleetSummary {
    total: number;
    onboardingIncomplete: number;
    whatsappDisconnected: number;
    subscriptionUnhealthy: number;
    dormant: number;
    totalMembers: number;
    totalContacts: number;
    recentSends: number;
}

export interface OrgMember {
    uid: string;
    email: string | null;
    displayName: string | null;
    role: string | null;
    isBillingAdministrator: boolean;
    disabled: boolean;
    lastSignInAt: number | null;
}

export interface OrgCampaign {
    id: string;
    templateName: string | null;
    status: string | null;
    type: string | null;
    totalRecipients: number;
    stats: { sent?: number; delivered?: number; read?: number; failed?: number };
    sentAt: number | null;
    sentBy: { userId?: string; email?: string; name?: string } | null;
}

/** A campaign row in the console's history table. */
export interface AdminCampaignRow {
    id: string;
    templateName: string | null;
    messageKind: string | null;
    status: string | null;
    type: string | null;
    listName: string | null;
    fromPhoneNumber: string | null;
    totalRecipients: number;
    stats: { sent?: number; delivered?: number; read?: number; failed?: number };
    sentAt: number | null;
    completedAt: number | null;
    sentBy: { userId?: string; email?: string; name?: string } | null;
    apiAppName: string | null;
}

/**
 * One recipient of a campaign, as the console is allowed to see them.
 *
 * The name is not here at all and the number arrives already masked — both are
 * removed server-side, so the real values never reach the browser. Everything
 * that remains is diagnostic.
 */
export interface AdminCampaignRecipient {
    id: string;
    phoneNumber: string | null;
    status: string | null;
    error: string | null;
    errorCode: number | string | null;
    errorSubcode: number | string | null;
    errorDetail: string | null;
    fbtraceId: string | null;
    sentAt: number | null;
    deliveredAt: number | null;
    readAt: number | null;
    hasReplied: boolean;
}

export interface AdminCampaignDetail extends AdminCampaignRow {
    templateLanguage: string | null;
    category: string | null;
    messageBody: string | null;
    messageHeader: string | null;
    messageFooter: string | null;
    headerFormat: string | null;
}

export interface AdminScheduledMessage {
    id: string;
    templateName: string | null;
    listName: string | null;
    status: string | null;
    recurrence: string | null;
    scheduledAt: number | null;
    nextRunAt: number | null;
    lastRunAt: number | null;
    recipientCount: number;
}

export interface AdminSchedule {
    id: string;
    name: string | null;
    description: string | null;
    templateName: string | null;
    listName: string | null;
    trigger: string | null;
    frequency: string | null;
    runTime: string | null;
    timezone: string | null;
    enabled: boolean;
    pausedReason: string | null;
    lastRunAt: number | null;
}

export interface AdminAutomation {
    id: string;
    name: string | null;
    description: string | null;
    triggerType: string | null;
    templateName: string | null;
    enabled: boolean;
    lastRunAt: number | null;
}

export interface AdminTemplate {
    id: string;
    name: string | null;
    language: string | null;
    status: string | null;
    category: string | null;
    qualityScore: any;
    rejectedReason: string | null;
    /** Meta's own component structure, rendered by the preview component. */
    components: any[];
    headerFormat: string | null;
    headerMediaUrl: string | null;
    createdAt: number | null;
    updatedAt: number | null;
}

export interface PlatformAdminRow {
    /**
     * The Firestore document id — either a Firebase uid or an email address.
     * Entries can be created before their owner has ever signed in, which is
     * what an email-keyed one is for, so this is the only identifier always
     * present. Revocation must target it, not `uid`.
     */
    id: string;
    /** Filled in on first sign-in; null for an entry nobody has used yet. */
    uid: string | null;
    email: string | null;
    displayName: string | null;
    disabled: boolean;
    addedBy: string | null;
    addedAt: number | null;
}

export interface AuditEntry {
    id: string;
    actorUid: string;
    actorEmail: string | null;
    action: string;
    orgId: string | null;
    orgName: string | null;
    sessionId: string | null;
    detail: Record<string, unknown>;
    at: number | null;
}

/** The support session the signed-in administrator is currently inside, if any. */
export interface ImpersonationState {
    sessionId: string;
    orgId: string;
    orgName: string | null;
    expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class PlatformAdminService {

    private auth = inject(Auth);
    private firestore = inject(Firestore);
    private functions = inject(Functions);
    private orgContext = inject(OrgContextService);
    private injector = inject(EnvironmentInjector);

    private readonly listOrgsFn = httpsCallable(this.functions, 'adminListOrgs');
    private readonly getOrgFn = httpsCallable(this.functions, 'adminGetOrg');
    private readonly refreshOrgStatsFn = httpsCallable(this.functions, 'adminRefreshOrgStats');
    private readonly impersonateFn = httpsCallable(this.functions, 'adminImpersonateOrg');
    private readonly createHandoffFn = httpsCallable(this.functions, 'adminCreateSupportHandoff');
    private readonly redeemHandoffFn = httpsCallable(this.functions, 'adminRedeemSupportHandoff');
    private readonly endImpersonationFn = httpsCallable(this.functions, 'adminEndImpersonation');
    private readonly listAdminsFn = httpsCallable(this.functions, 'adminListPlatformAdmins');
    private readonly setAdminFn = httpsCallable(this.functions, 'adminSetPlatformAdmin');
    private readonly listAuditFn = httpsCallable(this.functions, 'adminListAuditLog');
    private readonly listCampaignsFn = httpsCallable(this.functions, 'adminListOrgCampaigns');
    private readonly getCampaignFn = httpsCallable(this.functions, 'adminGetOrgCampaign');
    private readonly listSchedulesFn = httpsCallable(this.functions, 'adminListOrgSchedules');
    private readonly listTemplatesFn = httpsCallable(this.functions, 'adminListOrgTemplates');
    private readonly discoverWabasFn = httpsCallable(this.functions, 'adminDiscoverWabas');
    private readonly linkWabaFn = httpsCallable(this.functions, 'adminLinkWaba');
    private readonly registerWabaPhoneFn = httpsCallable(this.functions, 'adminRegisterWabaPhone');
    // The delete pages through every row the organisation owns, which can run
    // well past the SDK's default 70s client timeout. Matches the server's 540s.
    private readonly deleteOrgFn = httpsCallable(this.functions, 'adminDeleteOrg', { timeout: 540_000 });
    private readonly syncPcoOrgFn = httpsCallable(this.functions, 'adminSyncPcoOrg');
    private readonly getPcoSyncFn = httpsCallable(this.functions, 'adminGetPcoSync');

    /** The active support session, kept here so the banner and sidebar agree. */
    private readonly _impersonation = signal<ImpersonationState | null>(null);
    readonly impersonation = this._impersonation.asReadonly();

    private readonly _isPlatformAdmin = signal(false);
    readonly isPlatformAdmin = this._isPlatformAdmin.asReadonly();

    /**
     * Read the signed-in user's platform-admin and support-session state from
     * their token.
     *
     * Called on app start and after every claim change. Both facts live on the
     * token rather than in Firestore so the banner can render without a read.
     */
    async loadState(): Promise<void> {
        // Wait for the first authState emission rather than reading
        // auth.currentUser, which is still null on a hard page load until
        // Firebase has restored the session from IndexedDB. Reading it
        // synchronously reported "not a platform admin" for a perfectly valid
        // administrator, which quietly hid the console link in the profile
        // menu — the same mistake the old platform-admin route guard made.
        const user = await firstValueFrom(authState(this.auth).pipe(take(1)));

        if (!user) {
            this._isPlatformAdmin.set(false);
            this._impersonation.set(null);
            return;
        }

        try {
            const token = await user.getIdTokenResult();
            this._isPlatformAdmin.set(token.claims['platformAdmin'] === true);

            if (token.claims['impersonating'] === true) {
                // Details beyond the flag are not on the token — orgName and
                // expiry would bloat every request for the sake of a banner —
                // so they come from the user document.
                const state = await this.readSessionFromProfile(user.uid);
                this._impersonation.set(state);
            } else {
                this._impersonation.set(null);
            }
        } catch (error) {
            console.error('Could not read platform admin state:', error);
            this._isPlatformAdmin.set(false);
            this._impersonation.set(null);
        }
    }

    async listOrgs(): Promise<{ orgs: OrgStats[]; summary: FleetSummary }> {
        const result = await this.listOrgsFn({});
        return result.data as { orgs: OrgStats[]; summary: FleetSummary };
    }

    async getOrg(orgId: string): Promise<{ stats: OrgStats; members: OrgMember[]; campaigns: OrgCampaign[] }> {
        const result = await this.getOrgFn({ orgId });
        return result.data as { stats: OrgStats; members: OrgMember[]; campaigns: OrgCampaign[] };
    }

    async refreshOrgStats(orgId: string): Promise<OrgStats> {
        const result = await this.refreshOrgStatsFn({ orgId });
        return (result.data as { stats: OrgStats }).stats;
    }

    /**
     * Enter a customer's organisation, then reload into it.
     *
     * The token refresh is not optional. `orgId` is a custom claim, and both
     * Firestore rules and OrgContextService read it from there — without
     * getIdToken(true) the browser would keep sending the old token and every
     * read would be denied.
     *
     * The reload is not laziness either. Entering a session changes which
     * organisation the whole app is looking at, and a lot of state is cached
     * for the lifetime of a session on the assumption that it never does:
     * OrgContextService caches orgId and orgType, PermissionsService caches the
     * role, SubscriptionStateService tracks one organisation's plan, and the
     * sidebar builds its menu once. Forcing each of them to re-read
     * individually would be a list that quietly falls out of date as services
     * are added. Reloading makes the new organisation the only state there is.
     * @param orgId The organisation to enter.
     * @param reason Why — recorded in the audit trail.
     */
    async impersonate(orgId: string, reason: string): Promise<ImpersonationState> {
        const result = await this.impersonateFn({ orgId, reason });
        const session = result.data as ImpersonationState;

        await this.refreshClaims();
        this._impersonation.set(session);

        window.location.href = '/dashboard';
        return session;
    }

    /**
     * Leave a customer's organisation and return to the console.
     *
     * Reloads for the same reason entering does — see impersonate above, and
     * note the console now lives on its own origin, so this is a cross-site
     * navigation rather than a route change.
     */
    async endImpersonation(): Promise<void> {
        await this.endImpersonationFn({});

        await this.refreshClaims();
        this.orgContext.reset();
        this._impersonation.set(null);

        // The console is a different origin now, so this cannot be a route.
        // Falls back to the dashboard where no console is configured (outside
        // production) — the session has already ended either way, which is the
        // part that matters.
        window.location.href = environment.adminConsoleUrl || '/dashboard';
    }

    /**
     * Mint a one-time code carrying a support session to the customer origin.
     *
     * Called from the console, which cannot start the session itself: Firebase
     * Auth sessions are per-origin, so the administrator is anonymous on
     * echo.solenex.it and a session started here would strand them at a login
     * screen for a customer account they have no reason to own.
     * @param orgId The organisation to enter.
     * @param reason Why — stored with the handoff, never put in the URL.
     */
    async createSupportHandoff(orgId: string, reason: string): Promise<string> {
        const result = await this.createHandoffFn({ orgId, reason });
        return (result.data as { token: string }).token;
    }

    /**
     * Exchange a handoff code for the material needed to sign in.
     *
     * Runs on the customer origin, before there is any session — so unlike
     * every other call here, this one goes out unauthenticated. The code is
     * what authenticates it.
     */
    async redeemSupportHandoff(token: string): Promise<{
        customToken: string; orgId: string; reason: string;
    }> {
        const result = await this.redeemHandoffFn({ token });
        return result.data as { customToken: string; orgId: string; reason: string };
    }

    async listAdmins(): Promise<PlatformAdminRow[]> {
        const result = await this.listAdminsFn({});
        return (result.data as { admins: PlatformAdminRow[] }).admins;
    }

    async setAdmin(input: {
        /** The allowlist document id, from PlatformAdminRow.id. */
        id: string;
        email?: string | null;
        displayName?: string | null;
        disabled: boolean;
    }): Promise<void> {
        await this.setAdminFn(input);
    }

    /**
     * A page of an organisation's campaigns.
     *
     * `type` and the date range are applied server-side, matching the customer
     * history page; status and free-text search are filtered in the component,
     * also matching it.
     */
    async listCampaigns(options: {
        orgId: string;
        type?: string | null;
        dateFrom?: number | null;
        dateTo?: number | null;
        pageSize?: number;
        cursor?: string | null;
    }): Promise<{ campaigns: AdminCampaignRow[]; nextCursor: string | null }> {
        const result = await this.listCampaignsFn({
            orgId: options.orgId,
            type: options.type ?? null,
            dateFrom: options.dateFrom ?? null,
            dateTo: options.dateTo ?? null,
            pageSize: options.pageSize ?? 5,
            cursor: options.cursor ?? null
        });
        return result.data as { campaigns: AdminCampaignRow[]; nextCursor: string | null };
    }

    /** One campaign and a page of its recipients, identities already removed. */
    async getCampaign(options: {
        orgId: string;
        campaignId: string;
        statusFilter?: string | null;
        pageSize?: number;
        cursor?: string | null;
    }): Promise<{
        campaign: AdminCampaignDetail;
        recipients: AdminCampaignRecipient[];
        nextCursor: string | null;
    }> {
        const result = await this.getCampaignFn({
            orgId: options.orgId,
            campaignId: options.campaignId,
            statusFilter: options.statusFilter ?? null,
            pageSize: options.pageSize ?? 50,
            cursor: options.cursor ?? null
        });
        return result.data as {
            campaign: AdminCampaignDetail;
            recipients: AdminCampaignRecipient[];
            nextCursor: string | null;
        };
    }

    /** Everything the organisation has set to send by itself, across all three stores. */
    async listSchedules(orgId: string): Promise<{
        scheduledMessages: AdminScheduledMessage[];
        schedules: AdminSchedule[];
        automations: AdminAutomation[];
    }> {
        const result = await this.listSchedulesFn({ orgId });
        return result.data as {
            scheduledMessages: AdminScheduledMessage[];
            schedules: AdminSchedule[];
            automations: AdminAutomation[];
        };
    }

    async listTemplates(orgId: string): Promise<AdminTemplate[]> {
        const result = await this.listTemplatesFn({ orgId });
        return (result.data as { templates: AdminTemplate[] }).templates;
    }

    async listAuditLog(options: { orgId?: string | null; limit?: number } = {}): Promise<AuditEntry[]> {
        const result = await this.listAuditFn({
            orgId: options.orgId ?? null,
            limit: options.limit ?? 100
        });
        return (result.data as { entries: AuditEntry[] }).entries;
    }

    /**
     * Mint a new ID token so the claims written server-side take effect.
     *
     * Explicit about a missing user rather than optional-chaining past it. The
     * callers reload the page immediately afterwards, so silently skipping the
     * refresh would reload with the *old* claims — leaving the administrator
     * pointed at the wrong organisation with no indication anything failed.
     * Better to surface it and let them retry.
     */
    private async refreshClaims(): Promise<void> {
        const user = this.auth.currentUser;
        if (!user) {
            throw new Error('Signed out while changing support session. Please sign in again.');
        }

        await user.getIdToken(true);
    }

    /**
     * Read the running session's details from the administrator's own user
     * document — the one document they can always read, whichever organisation
     * their claims currently point at.
     */
    private async readSessionFromProfile(uid: string): Promise<ImpersonationState | null> {
        const snap = await runInInjectionContext(
            this.injector,
            () => getDoc(doc(this.firestore, 'users', uid))
        );
        const impersonating = snap.data()?.['impersonating'];

        if (!impersonating?.sessionId) return null;

        return {
            sessionId: impersonating.sessionId,
            orgId: impersonating.orgId,
            orgName: impersonating.orgName ?? null,
            expiresAt: impersonating.expiresAt?.toMillis?.() ?? Date.now()
        };
    }

    // -----------------------------------------------------------------------
    // Linking a WhatsApp account Embedded Signup cannot onboard
    // -----------------------------------------------------------------------

    /** List the WhatsApp accounts the operator token can reach. */
    async discoverWabas(opts: { wabaIds?: string[]; businessId?: string } = {}) {
        const result: any = await this.discoverWabasFn(opts);
        return result.data.wabas as AdminDiscoveredWaba[];
    }

    /**
     * Link an account to an organisation, or preview what linking would do.
     *
     * `apply` defaults to false. The plan a preview returns is exactly what
     * applying performs, so what the operator approves is the real thing.
     */
    async linkWaba(input: {
        organizationId: string;
        wabaIds: string[];
        phoneNumberIds?: string[];
        apply?: boolean;
    }) {
        const result: any = await this.linkWabaFn({ apply: false, ...input });
        return result.data.plan as AdminLinkPlan;
    }

    /** Register a number that sits on a WABA but was never registered. */
    async registerWabaPhone(organizationId: string, phoneNumberId: string, pin: string) {
        await this.registerWabaPhoneFn({ organizationId, phoneNumberId, pin });
    }

    /**
     * Start a Planning Center refresh for an organisation, on the token of
     * whoever set up its webhooks. Returns once queued; the run is async.
     */
    async syncPcoOrg(orgId: string) {
        await this.syncPcoOrgFn({ orgId });
    }

    /** The organisation's Planning Center sync, read live. */
    async getPcoSync(orgId: string) {
        const result: any = await this.getPcoSyncFn({ orgId });
        return result.data.pcoSync as PcoSyncSummary | null;
    }

    /**
     * What deleting an organisation would remove, and why the console would
     * refuse. Read-only.
     */
    async previewDeleteOrg(orgId: string) {
        const result: any = await this.deleteOrgFn({ orgId, apply: false });
        return result.data as AdminOrgDeletionPreview;
    }

    /**
     * Delete an organisation. `confirmName` must match its name; the server
     * checks it again and refuses anything with WhatsApp linked.
     */
    async deleteOrg(orgId: string, confirmName: string) {
        const result: any = await this.deleteOrgFn({ orgId, apply: true, confirmName });
        return result.data.report as AdminOrgDeletionReport;
    }
}

/** Everything deleting an organisation would remove. Mirrors OrgDeletionInventory. */
export interface AdminOrgDeletionInventory {
    orgId: string;
    exists: boolean;
    name: string | null;
    orgType: 'pco' | 'standalone';
    createdAt: string | null;
    members: { uid: string; email: string | null; role: string | null; platformAdmin: boolean }[];
    whatsapp: { wabaIds: string[]; phoneNumbers: { id: string; display: string | null }[] };
    pcoWebhooks: { count: number; ownerUid: string | null };
    sms: { provider: string | null };
    subscription: { status: string | null; planId: string | null; subscriptionCode: string | null };
    apiApps: number;
    activity: { campaigns: number; conversations: number; contacts: number };
    openImpersonationSessions: { id: string; actorUid: string; actorEmail: string | null }[];
    topLevelRows: Record<string, number>;
    storageObjects: Record<string, number>;
    templateMediaFiles: string[];
    blockers: string[];
}

/** The preview: the inventory, and every reason the console will not delete it. */
export interface AdminOrgDeletionPreview {
    inventory: AdminOrgDeletionInventory;
    refusals: string[];
}

/** What a deletion did, step by step. */
export interface AdminOrgDeletionReport {
    orgId: string;
    orgName: string | null;
    steps: { step: string; ok: boolean; detail: string }[];
    notHandled: string[];
}

/** A WhatsApp account the operator token can reach. */
export interface AdminDiscoveredWaba {
    id: string;
    name: string | null;
    relation: string;
    currency: string | null;
    ownerBusinessId: string | null;
    ownerBusinessName: string | null;
    phoneNumbers: any[];
    error?: string;
}

/** What linking would do, or did. */
export interface AdminLinkPlan {
    organizationId: string;
    organizationName: string;
    wabaIds: string[];
    phoneNumbers: any[];
    billingCurrency: string | null;
    businessId: string | null;
    /** WABAs this organisation is linked to now — linking replaces them. */
    replacing: string[];
    applied: boolean;
    subscribed?: { wabaId: string; ok: boolean; error?: string }[];
}
